import {
    Alert,
    Button,
    Col,
    DatePicker,
    Form,
    Row,
    Select,
    Spin
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { getAllCajas } from "../../services/cajaService";
import { getAllTurnos } from "../../services/turnoService";
import { getAllTPVs } from "../../services/tpvService";
import { useSyncHistoricos } from "../../hooks/useSyncHistoricos"
import "../CierreDeCaja/CierreDeCajaStyles.css";

const { Option } = Select;


const TraerHistoricosForm = ({ handleCancel, handleHistoricosSynced, onRefreshDashboard, country }) => {
    const [form] = Form.useForm();
    const [turnos, setTurnos] = useState([]);
    const [cajas, setCajas] = useState([]);
    const [tpvs, setTPVs] = useState([]);
    const selectedTPV = Form.useWatch("TPV", form);

    const {
        loading,
        setLoading,
        error,
        success,
        setError,
        setSuccess,
        handleSubmit
    } = useSyncHistoricos(handleHistoricosSynced, country);

    const handleFormSubmit = async (values) => {
        await handleSubmit(values);
        onRefreshDashboard?.();
    };

    useEffect(() => {
        // reset Caja cuando Tienda cambia
        form.setFieldsValue({ Caja: undefined });
    }, [selectedTPV]);

    useEffect(() => {
        setLoading(true);
        const cargarDatos = async () => {
            try {
                const cajasData = await getAllCajas();
                setCajas(cajasData);

                const turnosData = await getAllTurnos();
                setTurnos(turnosData);

                const tpvList = await getAllTPVs();
                setTPVs(tpvList);

            } catch (error) {
                console.error("Error al cargar datos:", error);
                setError("Error al cargar datos de turnos y cajas");
            }
            finally {
                setLoading(false);
            }
        };
        cargarDatos();
    }, []);

    return (
        <div>
            <h2>Sincronizar datos</h2>

            {error && (
                <Alert
                    message="Error"
                    description={error}
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                    closable
                    onClose={() => setError("")}
                />
            )}
            {success && (
                <Alert
                    message="Sincronización exitosa"
                    description={success}
                    type="success"
                    showIcon
                    style={{ marginBottom: 16 }}
                    closable
                    onClose={() => setSuccess("")}
                />
            )}

            <Spin spinning={loading} tip="Cargando...">
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleFormSubmit}
                >
                    <div className="cierreDeCaja-header-section">
                        <h3>Información general</h3>
                        <Row gutter={16}>
                            <p>⚠️ Verifique que no existan facturas en estado "Pendiente" de sincronizar. Una vez aplicados y confirmados los filtros, estos no podrán recuperarse.</p>
                        </Row>

                        <div className="multi-currency-section">
                            <h3>Filtros</h3>
                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="TPV"
                                        label="Tienda"
                                        rules={[
                                            { required: true, message: "Seleccione una tienda" },
                                        ]}
                                    >
                                        <Select
                                            placeholder="Seleccione una tienda"
                                            loading={loading}
                                            allowClear
                                            onChange={() => {
                                                // reset Caja cuando Tienda cambia
                                                form.setFieldsValue({ Caja: undefined });
                                            }}
                                        >
                                            {tpvs.map((tpv) => (
                                                <Option key={tpv.No} value={tpv.No}>
                                                    {tpv.nombre}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="Caja"
                                        label="Caja"
                                        rules={[{ required: true, message: "Seleccione la caja" }]}
                                        shouldUpdate={(prev, curr) => prev.TPV !== curr.TPV}
                                    >
                                        <Select
                                            placeholder="Seleccione una caja"
                                            loading={loading}
                                            allowClear
                                            optionLabelProp="label"
                                            disabled={!selectedTPV}
                                        >
                                            {cajas
                                                .filter((caja) => caja.TPV === selectedTPV)
                                                .map((caja) => (
                                                    <Option
                                                        key={caja.No}
                                                        value={caja.No}
                                                        label={`${caja.No} - ${caja.nombre}`}
                                                    >
                                                        {`${caja.No} - ${caja.nombre}`}
                                                    </Option>
                                                ))}
                                        </Select>
                                    </Form.Item>
                                </Col>

                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="FechaInicio"
                                        label="Fecha inicio"
                                        rules={[{ required: true, message: "Seleccione la fecha inicio" }]}>
                                        <DatePicker
                                            format="DD/MM/YYYY"
                                            style={{ width: "100%" }}
                                            disabledDate={(current) => current && current > dayjs().endOf("day")} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="FechaFin"
                                        label="Fecha fin"
                                        dependencies={["FechaInicio"]}
                                        rules={[
                                            { required: true, message: "Seleccione la fecha fin" },
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    const fechaInicio = getFieldValue("FechaInicio");
                                                    if (!fechaInicio) {
                                                        return Promise.reject(
                                                            new Error("Debe seleccionar la fecha inicio primero")
                                                        );
                                                    }
                                                    if (value && value.isAfter(dayjs(), "day")) {
                                                        return Promise.reject(
                                                            new Error("La fecha fin no puede ser futura")
                                                        );
                                                    }
                                                    if (
                                                        value &&
                                                        value.isAfter(dayjs(fechaInicio).add(180, "day"), "day")
                                                    ) {
                                                        return Promise.reject(
                                                            new Error(
                                                                "La fecha fin no puede ser más de 180 días después de la fecha inicio"
                                                            )
                                                        );
                                                    }
                                                    return Promise.resolve();
                                                },
                                            }),
                                        ]}
                                    >
                                        <DatePicker
                                            format="DD/MM/YYYY"
                                            style={{ width: "100%" }}
                                            disabledDate={(current) => {
                                                const fechaInicio = form.getFieldValue("FechaInicio");
                                                if (!fechaInicio) {
                                                    return true; // Deshabilitar todo hasta que elija inicio
                                                }
                                                return (
                                                    current < dayjs(fechaInicio).startOf("day") || // antes de inicio
                                                    current > dayjs().endOf("day") || // después de hoy
                                                    current > dayjs(fechaInicio).add(180, "day").endOf("day") // max de 180 días
                                                );
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="Turno"
                                        label="Turno"
                                    >
                                        <Select
                                            placeholder="Seleccione un turno"
                                            loading={loading}
                                            allowClear
                                        >
                                            {turnos.map((turno) => (
                                                <Option key={turno.No} value={turno.No}>
                                                    {turno.DescripcionTurno}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <div className="cierreDeCaja-form-actions">
                        <Button onClick={handleCancel}>Cancelar</Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                        >
                            Aceptar
                        </Button>
                    </div>
                </Form>
            </Spin>
        </div>
    );
};

export default TraerHistoricosForm;
